import { useEffect, useMemo, useState } from 'react';
import { send } from '@/shared/messages';
import type { Chapter } from '@/shared/types';
import { Button } from '@/components/Button';
import { Alert } from '@/components/Alert';
import { Card } from '@/components/Card';
import { Input, Select } from '@/components/Field';
import { SegmentedControl } from '@/components/SegmentedControl';

interface PageEntry {
  // Stable per-render id for React keys; not persisted.
  id: string;
  title: string;
  url: string;
  markdown: string;
  breadcrumbs: string[];
}

interface CapturedBundle {
  chapterTitle: string;
  breadcrumbs: string[];
  sourceUrl: string;
  pages: PageEntry[];
  transcriptCues: { start: number; end: number; text: string }[];
  videos: { srcUrl: string; durationSec: number }[];
  warning?: string;
}

type Phase = 'idle' | 'capturing' | 'review' | 'submitting';

export function CaptureRoute({
  onCaptured,
  activeTestId,
}: {
  onCaptured: (chapterId: string) => void;
  activeTestId: string | null;
}) {
  const [tab, setTab] = useState<'auto' | 'paste'>('auto');
  return (
    <div className="p-4">
      <SegmentedControl
        size="sm"
        className="mb-3"
        options={[
          { value: 'auto', label: 'Auto-capture' },
          { value: 'paste', label: 'Paste' },
        ]}
        value={tab}
        onChange={setTab}
      />
      {tab === 'auto' ? (
        <AutoCapture onCaptured={onCaptured} activeTestId={activeTestId} />
      ) : (
        <PasteCapture onCaptured={onCaptured} activeTestId={activeTestId} />
      )}
    </div>
  );
}

function AutoCapture({
  onCaptured,
  activeTestId,
}: {
  onCaptured: (id: string) => void;
  activeTestId: string | null;
}) {
  const [activeTab, setActiveTab] = useState<chrome.tabs.Tab | null>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState<{ pageNum: number; status: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [bundle, setBundle] = useState<CapturedBundle | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function refresh() {
      const tabs = await chrome.tabs.query({ active: true, currentWindow: true });
      if (cancelled) return;
      setActiveTab(tabs[0] ?? null);
    }
    void refresh();
    const onActivated = () => void refresh();
    const onUpdated = (_id: number, _ch: chrome.tabs.TabChangeInfo, t: chrome.tabs.Tab) => {
      if (t.active) void refresh();
    };
    const onFocus = () => void refresh();
    chrome.tabs.onActivated.addListener(onActivated);
    chrome.tabs.onUpdated.addListener(onUpdated);
    chrome.windows.onFocusChanged.addListener(onFocus);
    return () => {
      cancelled = true;
      chrome.tabs.onActivated.removeListener(onActivated);
      chrome.tabs.onUpdated.removeListener(onUpdated);
      chrome.windows.onFocusChanged.removeListener(onFocus);
    };
  }, []);

  useEffect(() => {
    function listener(msg: { type?: string; pageNum?: number; status?: string }) {
      if (msg?.type === 'CAPTURE_PROGRESS') {
        setProgress({ pageNum: msg.pageNum ?? 0, status: msg.status ?? '' });
      }
    }
    chrome.runtime.onMessage.addListener(listener);
    return () => chrome.runtime.onMessage.removeListener(listener);
  }, []);

  async function start() {
    if (!activeTab?.id || !activeTab?.url) {
      setError('No active tab.');
      return;
    }
    const tabId = activeTab.id;
    const origin = `${new URL(activeTab.url).origin}/*`;

    let granted: boolean;
    try {
      granted = await chrome.permissions.request({ origins: [origin] });
    } catch (e) {
      setError(`Permission request failed: ${e instanceof Error ? e.message : String(e)}`);
      return;
    }
    if (!granted) {
      setError(`Chrome permission for ${origin} was denied.`);
      return;
    }

    setError(null);
    setProgress({ pageNum: 0, status: 'starting…' });
    setPhase('capturing');
    try {
      const r = (await chrome.runtime.sendMessage({
        type: 'CAPTURE_CHAPTER',
        tabId,
      })) as
        | {
            chapterTitle: string;
            breadcrumbs: string[];
            sourceUrl: string;
            pages: { title: string; url: string; markdown: string; breadcrumbs: string[] }[];
            transcriptCues: { start: number; end: number; text: string }[];
            videos: { srcUrl: string; durationSec: number }[];
            warning?: string;
          }
        | { error: string };
      if ('error' in r) {
        setError(r.error);
        setPhase('idle');
        return;
      }
      setBundle({
        chapterTitle: r.chapterTitle,
        breadcrumbs: r.breadcrumbs,
        sourceUrl: r.sourceUrl,
        pages: r.pages.map((p, i) => ({ ...p, id: `p-${Date.now()}-${i}` })),
        transcriptCues: r.transcriptCues,
        videos: r.videos,
        warning: r.warning,
      });
      setPhase('review');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setPhase('idle');
    } finally {
      setProgress(null);
    }
  }

  async function stop() {
    if (!activeTab?.id) return;
    try {
      await send({ type: 'STOP_CAPTURE', tabId: activeTab.id });
    } catch {
      /* best effort */
    }
  }

  if (phase === 'review' && bundle) {
    return (
      <ReviewScreen
        bundle={bundle}
        activeTestId={activeTestId}
        onChange={setBundle}
        onSubmitted={(id) => {
          onCaptured(id);
          setBundle(null);
          setPhase('idle');
        }}
        onDiscard={() => {
          setBundle(null);
          setError(null);
          setPhase('idle');
        }}
      />
    );
  }

  if (phase === 'submitting') {
    return (
      <div className="space-y-2 text-sm text-slate-600">
        <p>Generating summary, sections, and flashcards… typically 15–30s.</p>
      </div>
    );
  }

  const hostHint = activeTab?.url ? new URL(activeTab.url).hostname : 'no tab';

  return (
    <div className="space-y-3 text-sm">
      <ol className="space-y-1.5 rounded border border-blue-100 bg-blue-50 p-3 text-xs text-blue-950">
        <li className="flex gap-2">
          <span className="font-semibold">1.</span>
          <span>
            On the LMS, open the chapter to its <strong>very first page</strong>. Capture
            walks Next from there until it hits the end of the chapter.
          </span>
        </li>
        <li className="flex gap-2">
          <span className="font-semibold">2.</span>
          <span>
            Click <strong>Capture this chapter</strong> below. Watch the progress; the
            page count climbs as it works.
          </span>
        </li>
        <li className="flex gap-2">
          <span className="font-semibold">3.</span>
          <span>
            <strong>Review</strong> the captured pages — titles, char counts, optional
            inline edits. Trim or remove pages that don't belong.
          </span>
        </li>
        <li className="flex gap-2">
          <span className="font-semibold">4.</span>
          <span>
            Scroll to the bottom, pick <strong>+ New chapter</strong> or an existing one
            in the <em>Attach to</em> dropdown, then click <strong>Save</strong>.
          </span>
        </li>
      </ol>
      <div className="rounded bg-slate-50 p-2 text-xs text-slate-700">
        Active tab: <span className="font-mono">{hostHint}</span>
      </div>
      {phase === 'capturing' ? (
        <div className="space-y-2">
          <div className="rounded bg-blue-50 p-2 text-xs text-blue-900">
            {progress
              ? `Page ${progress.pageNum} · ${progress.status}`
              : 'Capturing…'}
          </div>
          <Button variant="danger" size="lg" block className="bg-card" onClick={stop}>
            Stop capture
          </Button>
        </div>
      ) : (
        <Button variant="primary" size="lg" block onClick={start} disabled={!activeTab?.id}>
          Capture this chapter
        </Button>
      )}
      {error && (
        <Alert>
          {error}
          <div className="mt-1 text-slate-500">
            If selectors look wrong, edit them in <em>Options</em> and click "Test on active
            tab" to validate.
          </div>
        </Alert>
      )}
    </div>
  );
}

function ReviewScreen({
  bundle,
  activeTestId,
  onChange,
  onSubmitted,
  onDiscard,
}: {
  bundle: CapturedBundle;
  activeTestId: string | null;
  onChange: (b: CapturedBundle) => void;
  onSubmitted: (chapterId: string) => void;
  onDiscard: () => void;
}) {
  const [titleEdit, setTitleEdit] = useState(bundle.chapterTitle);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [target, setTarget] = useState<'new' | string>('new');

  useEffect(() => {
    // Scope the "attach to existing chapter" list to the active test so the
    // target dropdown matches where a new chapter would land.
    send({ type: 'LIST_CHAPTERS', testId: activeTestId ?? undefined }).then((r) =>
      setChapters(r.chapters)
    );
  }, [activeTestId]);

  const composedMd = useMemo(() => composeMarkdown(bundle.breadcrumbs, bundle.pages), [
    bundle.breadcrumbs,
    bundle.pages,
  ]);
  const totalChars = composedMd.length;

  function updatePage(id: string, patch: Partial<PageEntry>) {
    onChange({
      ...bundle,
      pages: bundle.pages.map((p) => (p.id === id ? { ...p, ...patch } : p)),
    });
  }
  function removePage(id: string) {
    onChange({ ...bundle, pages: bundle.pages.filter((p) => p.id !== id) });
  }
  function trimAfter(idx: number) {
    onChange({ ...bundle, pages: bundle.pages.slice(0, idx + 1) });
  }
  function move(idx: number, delta: number) {
    const next = [...bundle.pages];
    const j = idx + delta;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange({ ...bundle, pages: next });
  }
  function toggleOpen(id: string) {
    const s = new Set(openIds);
    if (s.has(id)) s.delete(id);
    else s.add(id);
    setOpenIds(s);
  }

  async function submit() {
    if (bundle.pages.length === 0) {
      setError('No pages left to ingest. Discard and recapture, or undo your removals.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const r = await send({
        type: 'ATTACH_TEXT_CAPTURE',
        title: titleEdit || bundle.chapterTitle,
        sourceUrl: bundle.sourceUrl,
        markdown: composedMd,
        transcriptCues: bundle.transcriptCues,
        videos: bundle.videos,
        targetChapterId: target === 'new' ? undefined : target,
        targetTestId: activeTestId ?? undefined,
      });
      onSubmitted(r.chapterId);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-3 text-sm">
      <Card padding="none" className="p-3">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-700">Chapter title</span>
          <Input
            value={titleEdit}
            onChange={(e) => setTitleEdit(e.target.value)}
            className="w-full"
          />
        </label>
        {bundle.breadcrumbs.length > 1 && (
          <div className="mt-2 text-xs text-slate-500">
            Breadcrumb path: <span className="font-mono">{bundle.breadcrumbs.join(' › ')}</span>
          </div>
        )}
        <div className="mt-3 grid grid-cols-2 gap-1 text-xs text-slate-700">
          <div>Pages</div><div className="font-mono">{bundle.pages.length}</div>
          <div>Total chars</div><div className="font-mono">{totalChars.toLocaleString()}</div>
          <div>~Tokens</div><div className="font-mono">~{Math.round(totalChars / 4).toLocaleString()}</div>
          <div>Videos</div><div className="font-mono">{bundle.videos.length}</div>
          <div>Transcript cues</div><div className="font-mono">{bundle.transcriptCues.length}</div>
        </div>
        {bundle.warning && (
          <Alert tone="warning" className="mt-2">
            ⚠ {bundle.warning}
          </Alert>
        )}
      </Card>

      <div className="space-y-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Pages ({bundle.pages.length})
        </div>
        {bundle.pages.length === 0 && (
          <div className="rounded border border-dashed border-slate-300 p-3 text-xs text-slate-500">
            No pages. Discard and recapture if this is wrong.
          </div>
        )}
        {bundle.pages.map((p, idx) => {
          const isOpen = openIds.has(p.id);
          return (
            <Card key={p.id} padding="none">
              <div className="flex items-start gap-2 p-2">
                <div className="flex flex-col text-xs">
                  <button
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                    className="px-1 text-slate-500 disabled:text-slate-300"
                    title="Move up"
                  >
                    ▲
                  </button>
                  <button
                    onClick={() => move(idx, 1)}
                    disabled={idx === bundle.pages.length - 1}
                    className="px-1 text-slate-500 disabled:text-slate-300"
                    title="Move down"
                  >
                    ▼
                  </button>
                </div>
                <div className="flex-1 space-y-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-xs font-mono text-slate-400">{idx + 1}</span>
                    <input
                      value={p.title}
                      onChange={(e) => updatePage(p.id, { title: e.target.value })}
                      className="flex-1 rounded border border-slate-200 px-2 py-0.5 text-sm"
                    />
                  </div>
                  <div className="text-[10px] text-slate-400">
                    {p.markdown.length.toLocaleString()} chars · {p.url.replace(/^https?:\/\//, '')}
                  </div>
                </div>
                <div className="flex flex-col gap-1 text-xs">
                  <Button size="xs" onClick={() => toggleOpen(p.id)}>
                    {isOpen ? 'Hide' : 'Edit'}
                  </Button>
                  <Button
                    size="xs"
                    onClick={() => trimAfter(idx)}
                    disabled={idx === bundle.pages.length - 1}
                    title="Drop all pages after this one"
                  >
                    Trim
                  </Button>
                  <Button variant="danger" size="xs" onClick={() => removePage(p.id)}>
                    Remove
                  </Button>
                </div>
              </div>
              {isOpen && (
                <textarea
                  value={p.markdown}
                  onChange={(e) => updatePage(p.id, { markdown: e.target.value })}
                  rows={14}
                  className="block w-full border-t border-slate-200 px-2 py-1 font-mono text-[11px]"
                />
              )}
            </Card>
          );
        })}
      </div>

      <details className="rounded border border-slate-200 bg-slate-50 p-2 text-xs">
        <summary className="cursor-pointer font-medium text-slate-700">
          Composed markdown preview ({composedMd.length.toLocaleString()} chars)
        </summary>
        <pre className="mt-2 max-h-72 overflow-auto whitespace-pre-wrap font-mono text-[11px] text-slate-700">
          {composedMd.slice(0, 6000)}
          {composedMd.length > 6000 ? `\n\n… (+${composedMd.length - 6000} more chars)` : ''}
        </pre>
      </details>

      <label className="block text-xs">
        <span className="text-slate-700">Attach to</span>
        <Select
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          className="mt-0.5 w-full"
        >
          <option value="new">+ New chapter</option>
          {chapters.map((c) => (
            <option key={c.id} value={c.id}>
              {c.title}
            </option>
          ))}
        </Select>
      </label>

      <div className="flex gap-2">
        <Button
          variant="primary"
          size="lg"
          className="flex-1"
          onClick={submit}
          disabled={bundle.pages.length === 0}
          loading={submitting}
        >
          {submitting
            ? 'Saving…'
            : target === 'new'
              ? `Save ${bundle.pages.length} page(s) as new chapter`
              : `Add ${bundle.pages.length} page(s) to chapter`}
        </Button>
        <Button size="lg" onClick={onDiscard} disabled={submitting}>
          Discard
        </Button>
      </div>
      {error && <Alert>{error}</Alert>}
    </div>
  );
}

function composeMarkdown(breadcrumbs: string[], pages: PageEntry[]): string {
  const head: string[] = [];
  if (breadcrumbs.length > 0) head.push(`# ${breadcrumbs[0]}`);
  if (breadcrumbs.length > 1) head.push(`_${breadcrumbs.slice(1).join(' › ')}_`);
  const body = pages
    .map((p) => `## ${p.title}\n\n${p.markdown}`.trim())
    .filter((s) => s.length > 0)
    .join('\n\n---\n\n');
  return [head.join('\n'), body].filter((s) => s.length > 0).join('\n\n');
}

function PasteCapture({
  onCaptured,
  activeTestId,
}: {
  onCaptured: (id: string) => void;
  activeTestId: string | null;
}) {
  const [title, setTitle] = useState('');
  const [format, setFormat] = useState<'chapter' | 'vtt'>('chapter');
  const [raw, setRaw] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);

  async function submit() {
    setPending(true);
    setError(null);
    setProgress('Calling the model — flashcard generation usually takes 15–30s…');
    try {
      const r = (await chrome.runtime.sendMessage({
        type: 'INGEST_PASTED',
        title,
        format,
        raw,
        targetTestId: activeTestId ?? undefined,
      })) as { chapterId: string; cardCount: number } | { error: string };
      if ('error' in r) {
        setError(r.error);
        return;
      }
      onCaptured(r.chapterId);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPending(false);
      setProgress(null);
    }
  }

  return (
    <div className="space-y-3 text-sm">
      <label className="block">
        <span className="mb-1 block font-medium text-slate-700">Chapter title</span>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Chapter 3 — Securities Markets"
          className="w-full"
        />
      </label>
      <div className="flex gap-3 text-xs">
        <label className="flex items-center gap-1">
          <input
            type="radio"
            checked={format === 'chapter'}
            onChange={() => setFormat('chapter')}
          />
          Chapter text
        </label>
        <label className="flex items-center gap-1">
          <input type="radio" checked={format === 'vtt'} onChange={() => setFormat('vtt')} />
          VTT subtitles
        </label>
      </div>
      <label className="block">
        <span className="mb-1 block font-medium text-slate-700">
          {format === 'chapter' ? 'Paste chapter text or markdown' : 'Paste VTT file contents'}
        </span>
        <textarea
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          rows={12}
          placeholder={
            format === 'chapter'
              ? 'Paste the chapter content here. Markdown is fine.'
              : 'WEBVTT\n\n00:00:00.000 --> 00:00:05.000\nFirst caption…'
          }
          className="w-full rounded border border-slate-300 px-2 py-1 font-mono text-xs"
        />
      </label>
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-500">
          {raw.length.toLocaleString()} chars · {Math.round(raw.length / 4)} ~tokens
        </span>
        <Button
          variant="primary"
          onClick={submit}
          disabled={raw.length < 200 || title.length < 1}
          loading={pending}
        >
          {pending ? 'Ingesting…' : 'Ingest'}
        </Button>
      </div>
      {progress && <div className="text-xs text-slate-500">{progress}</div>}
      {error && <Alert>{error}</Alert>}
    </div>
  );
}
